import { ChevronDown, ChevronUp, SquareArrowUpRight, FilePlus2, Printer, FileText, TriangleAlert } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { badgeClasses, convertISODateToMMDDYYYY, formatCurrency } from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';
import {
  getAccountsReceivable,
  useAccountsReceivableFilterOptions,
  useMarkAsFailedTransaction,
} from '@/services/accountsReceivableService';
import { bulkExportInvoices, mergeInvoices } from '@/services/invoiceService';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { buildHiInvoicePath } from '@/pages/homeInspection/invoice/hiInvoiceRoute';
import {
  AccountsReceivableQueryParams,
  AccountsReceivableInvoice,
  ARInvoiceStatus,
  AgingBracket,
} from '@/types/accountsReceivable.types';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { OneLineText } from '@/components/OneLineText';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { ApplyPaymentResponse } from '@/types/payment.types';
import { TooltipContent } from '@/components/ui/tooltip';
import { hasFeature } from '@/services/featureService';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type AgingType = 'PAID' | 'LESS_THAN_30' | '30_DAY' | '60_DAY' | '90_DAY';

const badges: Record<AgingType, { label: string; bgColor: string; fontColor: string; hoverColor: string }> = {
  LESS_THAN_30: {
    label: 'Unpaid',
    bgColor: '#FFEEBF',
    fontColor: '#6C4A09',
    hoverColor: '#FFEEBF',
  },
  PAID: {
    label: 'Paid',
    bgColor: '#D5F4E3',
    fontColor: '#1B8652',
    hoverColor: '#D5F4E3',
  },
  '30_DAY': {
    label: 'Unpaid',
    bgColor: '#FFE0E0',
    fontColor: '#A41919',
    hoverColor: '#FFE0E0',
  },
  '60_DAY': {
    label: 'Unpaid',
    bgColor: '#FFAD0D',
    fontColor: '#0A0A0A',
    hoverColor: '#FFAD0D',
  },
  '90_DAY': {
    label: 'Unpaid',
    bgColor: '#A41919',
    fontColor: '#F5F5F5',
    hoverColor: '#A41919',
  },
};

const resolveInvoiceRoute = (
  serviceType: string | null | undefined,
  invoiceNumber: string,
  workOrderId: string | null
) => {
  const qs = workOrderId ? `?workOrderId=${workOrderId}` : '';
  const normalizedServiceType = serviceType?.replace(/[^a-zA-Z]/g, '').toUpperCase() ?? '';
  if (normalizedServiceType.includes('DELIVERY')) {
    return `/delivery/invoice/${invoiceNumber}${qs}`;
  }
  if (normalizedServiceType.includes('HOMEINSPECTION') || normalizedServiceType.includes('HOUSEINSPECTION')) {
    return workOrderId
      ? buildHiInvoicePath(invoiceNumber, workOrderId, invoiceNumber)
      : `/home-inspection/invoice/${invoiceNumber}`;
  }
  if (normalizedServiceType.includes('DUEDILIGENCE')) {
    return `/due-diligence/invoice/${invoiceNumber}${qs}`;
  }
  return `/process/invoice/${invoiceNumber}${qs}`;
};

const AccountsReceivable = () => {
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [tableData, setTableData] = useState<{ data: AccountsReceivableInvoice[]; rest?: unknown }>();
  const [selectedAgingBracket, setSelectedAgingBracket] = useState<AgingBracket | undefined>(undefined);
  const [isApplyPaymentModalOpen, setIsApplyPaymentModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const hasApplyPaymentFeature = hasFeature('accounting.applyPayment');
  const queryClient = useQueryClient();
  const [summaryData, setSummaryData] = useState<{
    invoiceNumbers?: string[];
    totalAmountDue?: string;
    past30DaysDue?: string;
    past60DaysDue?: string;
    past90DaysDue?: string;
    totalRows?: number;
  }>({});

  const filterOptionsData = useAccountsReceivableFilterOptions();

  const { clientOptions, invoiceOptions, fileNumberOptions } = useMemo(() => {
    const clientOptions = filterOptionsData.data?.clients ?? [];
    const invoiceOptions = filterOptionsData.data?.invoiceNumbers ?? [];
    const fileNumberOptions = filterOptionsData.data?.fileNumbers ?? [];
    return {
      clientOptions,
      invoiceOptions,
      fileNumberOptions,
    };
  }, [filterOptionsData.data]);

  const apiGetWrapper = useCallback((params: Record<string, unknown>) => {
    const apiParams: AccountsReceivableQueryParams = {
      ...params,
      pageNo: params.pageNo as number,
      pageSize: params.pageSize as number,
    };
    const result = getAccountsReceivable(apiParams);

    return {
      data: result.data,
      totalRows: result.totalRows,
      isFetching: result.isFetching,
      invoiceNumbers: result.invoiceNumbers ?? [],
      totalAmountDue: result.totalAmountDue,
      past30DaysDue: result.past30DaysDue,
      past60DaysDue: result.past60DaysDue,
      past90DaysDue: result.past90DaysDue,
    };
  }, []);

  useEffect(() => {
    if (tableData?.rest) {
      const response = tableData.rest as {
        totalAmountDue?: string;
        past30DaysDue?: string;
        past60DaysDue?: string;
        invoiceNumbers?: string[];
        past90DaysDue?: string;
        totalRows?: number;
      };
      if (selectAll) {
        setSelectedInvoices(response.invoiceNumbers ?? []);
      }
      setSummaryData({
        invoiceNumbers: response.invoiceNumbers ?? undefined,
        totalAmountDue: response.totalAmountDue ?? undefined,
        past30DaysDue: response.past30DaysDue ?? undefined,
        past60DaysDue: response.past60DaysDue ?? undefined,
        past90DaysDue: response.past90DaysDue ?? undefined,
        totalRows: response.totalRows ?? undefined,
      });
    }
  }, [tableData]);

  const [openSecondaryColumns, setOpenSecondaryColumns] = useState<{ id: string; isExpanded: boolean }[]>([]);

  const handleCheckboxChange = (invoiceNumber: string, isChecked: string | boolean) => {
    if (isChecked) {
      setSelectedInvoices((prev) => [...prev, invoiceNumber]);
    } else {
      setSelectedInvoices((prev) => prev.filter((invoice) => invoice !== invoiceNumber));
    }
  };
  const handleSelectAllChange = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (isChecked) {
      const allInvoiceNumbers = summaryData.invoiceNumbers ?? [];
      setSelectedInvoices(allInvoiceNumbers);
    } else {
      setSelectedInvoices([]);
    }
  };

  const selectedInvoicesData = useMemo(() => {
    return (
      tableData?.data
        ?.filter((row) => selectedInvoices.includes(row.invoiceNumber))
        .map((row) => ({
          invoiceNumber: row.invoiceNumber,
          invoiceAmount: row.invoiceAmount,
          balance: row.balance,
          invoiceStatus: row.invoiceStatus,
          memo: row.memo,
        })) ?? []
    );
  }, [tableData, selectedInvoices]);

  const handleApplyPaymentSuccess = useCallback((data: ApplyPaymentResponse) => {
    setSelectedInvoices([]);
    setTableData((prev) => {
      if (!prev?.data) return prev;
      const updatedData = prev.data.map((d) => {
        const matchingInvoice = data.invoices.find((inv) => inv.invoiceNumber === d.invoiceNumber);
        if (matchingInvoice) {
          const isPaid = matchingInvoice.balance <= 0;
          return {
            ...d,
            invoiceStatus: isPaid ? ARInvoiceStatus.PAID : ARInvoiceStatus.UNPAID,
            balance: matchingInvoice.balance,
            adjustAmount: matchingInvoice.adjustAmount,
            memo: matchingInvoice.memo,
            isFailedTransaction: isPaid ? false : d.isFailedTransaction,
          };
        }
        return d;
      });
      return { ...prev, data: updatedData };
    });
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleSingleInvoiceApplyPayment = useCallback((invoice: AccountsReceivableInvoice) => {
    if (invoice.invoiceStatus === ARInvoiceStatus.UNPAID) {
      setSelectedInvoices([invoice.invoiceNumber]);
      setIsApplyPaymentModalOpen(true);
    }
  }, []);

  const markFailedMutation = useMarkAsFailedTransaction();

  const handleBulkExport = useCallback(async () => {
    if (selectedInvoices.length > 0) {
      await bulkExportInvoices(selectedInvoices);
    } else {
      toast({
        description: 'Please select at least one invoice to export',
      });
    }
  }, [selectedInvoices]);

  const handleBulkMerge = useCallback(
    async (invoiceNumbers: string[]) => {
      if (invoiceNumbers.length > 0) {
        const firstInvoice = tableData?.data?.find((d) => d.invoiceNumber === invoiceNumbers[0]);
        const clientName = firstInvoice?.clientName ?? undefined;
        await mergeInvoices(invoiceNumbers, clientName ?? undefined);
      } else {
        toast({
          description: 'Please select at least one invoice to merge',
        });
      }
    },
    [tableData]
  );

  const handleMarkAsFailed = useCallback(() => {
    if (selectedInvoices.length > 0) {
      markFailedMutation.mutate(
        { invoiceNumbers: selectedInvoices, isFailedTransaction: true },
        {
          onSuccess: (data) => {
            toast({ description: data.message });
            void queryClient.invalidateQueries({ queryKey: ['accounts-receivable'] });
            setSelectedInvoices([]);
            setSelectAll(false);
          },
          onError: (error) => {
            displayError(error.response?.data);
          },
        }
      );
    } else {
      toast({
        description: 'Please select at least one invoice to mark as failed',
      });
    }
  }, [selectedInvoices, markFailedMutation, queryClient]);

  const secondaryTableData = useMemo(() => {
    return tableData?.data?.map((d) => ({
      id: d.invoiceNumber,
      data: [d],
    }));
  }, [tableData]);

  const bulkActions = [
    {
      label: 'Bulk Payment',
      value: 'bulkPayment',
      icon: <SquareArrowUpRight className="w-4 h-4" color="#334155" strokeWidth="1px" />,
      onClick: () => {
        const paidInvoiceExists = selectedInvoicesData.some(
          (invoice) => invoice.invoiceStatus === ARInvoiceStatus.PAID
        );
        if (selectedInvoicesData.length > 0) {
          if (paidInvoiceExists) {
            toast({
              variant: 'destructive',
              description: 'Cannot apply payment to paid invoices',
            });
          } else {
            setIsApplyPaymentModalOpen(true);
          }
        } else {
          toast({
            variant: 'destructive',
            description: 'Please select at least one unpaid invoice for payment',
          });
        }
      },
    },
    {
      label: 'Merge Invoices',
      value: 'mergeInvoices',
      icon: <FilePlus2 className="w-4 h-4" color="#334155" strokeWidth="1px" />,
      onClick: async () => {
        await handleBulkMerge(selectedInvoices);
      },
    },
    {
      label: 'Export Invoices',
      value: 'exportInvoices',
      icon: <Printer className="w-4 h-4" color="#334155" strokeWidth="1px" />,
      onClick: async () => {
        await handleBulkExport();
      },
    },
    {
      label: 'Mark as Payment Failed',
      value: 'markAsFailed',
      icon: <FileText className="w-4 h-4" color="#334155" strokeWidth="1px" />,
      onClick: () => {
        handleMarkAsFailed();
      },
    },
  ];

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'clientIds',
        label: 'Client',
        placeholder: 'Select Clients',
        fieldType: 'MULTISELECT',
        multiSelectOptions: clientOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
        dependsOn: {
          field: 'fromDate',
          resetOnChange: true,
          disableBefore: true,
        },
      },
      {
        name: 'invoiceNumbers',
        label: 'Invoice Number',
        placeholder: 'Select Invoices',
        fieldType: 'MULTISELECT',
        multiSelectOptions: invoiceOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
      {
        name: 'fileNumbers',
        label: 'File Number',
        placeholder: 'Select File Numbers',
        fieldType: 'MULTISELECT',
        multiSelectOptions: fileNumberOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
    ],
    [clientOptions, invoiceOptions, fileNumberOptions]
  );

  const viewFilters = {
    buttonLabel: 'View',
    groups: [
      {
        label: 'Invoice Type',
        name: 'invoiceType',
        options: [
          { label: 'Pre-paid', value: 'PREPAID' },
          { label: '0-Balance', value: 'ZERO_BALANCE_INVOICE' },
        ],
      },
      {
        label: 'Status',
        name: 'status',
        options: [
          { label: 'Unpaid', value: 'UNPAID' },
          { label: 'Paid', value: 'PAID' },
        ],
      },
    ],
  };

  const handleAgingBracketClick = useCallback((bracket: AgingBracket) => {
    setSelectedAgingBracket((prev) => (prev === bracket ? undefined : bracket));
  }, []);

  const summarySection = useMemo(
    () => (
      <div className="w-full flex justify-between flex-col md:flex-row gap-4">
        <div className="w-[50%] flex justify-between flex-col md:justify-start gap-1.5">
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Amount Due: </span>
            <span className="text-[#551175] text-sm font-[500] ">
              <BillingAmount value={summaryData.totalAmountDue ? parseFloat(summaryData.totalAmountDue) : 0} />
            </span>
          </div>
          <div className="flex gap-3 flex-col md:flex-row">
            <span
              className={`flex gap-1 cursor-pointer hover:opacity-80 ${selectedAgingBracket === AgingBracket.DAY_30 ? 'font-[500] underline text-xs' : 'text-xs'}`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_30)}
            >
              <span className="font-[500] text-xs">30 Days: </span>
              <span className="text-[#551175] text-xs font-[500] ">
                <BillingAmount value={summaryData.past30DaysDue ? parseFloat(summaryData.past30DaysDue) : 0} />
              </span>
            </span>
            <span
              className={`flex gap-1 cursor-pointer hover:opacity-80 ${selectedAgingBracket === AgingBracket.DAY_60 ? 'font-[500] underline text-xs' : 'text-xs'}`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_60)}
            >
              <span className="font-[500] text-xs">60 Days: </span>
              <span className="text-[#FFAD0D] text-xs font-[500] ">
                <BillingAmount value={summaryData.past60DaysDue ? parseFloat(summaryData.past60DaysDue) : 0} />
              </span>
            </span>
            <span
              className={`flex gap-1 cursor-pointer hover:opacity-80 ${selectedAgingBracket === AgingBracket.DAY_90 ? 'font-[500] underline text-xs' : 'text-xs'}`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_90)}
            >
              <span className="font-[500] text-xs">90 Days: </span>
              <span className="text-[#A41919] text-xs font-[500] ">
                <BillingAmount value={summaryData.past90DaysDue ? parseFloat(summaryData.past90DaysDue) : 0} />
              </span>
            </span>
          </div>
          <div>
            <span className="text-sm">Showing this week&apos;s and overdue invoices.</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-6">
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              className="focus:border-[#1551B2] !border-[1px] !border-primary min-w-[136px] w-fit h-[36px] !text-primary font-[500] text-sm"
            >
              <Button variant="outline" className="flex flex-row gap-2 align-middle ">
                <span>Bulk Actions</span>
                <ChevronDown className="w-4 h-4" strokeWidth="1px" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mr-8">
              {bulkActions.map((action) => (
                <DropdownMenuItem key={action.value} onSelect={action.onClick}>
                  <div className="!flex !w-full !flex-row justify-between">
                    <span className="text-primary text-sm font-[500]">{action.label}</span>
                    <span>{action.icon}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-row ">
            <div className="text-xs leading-none self-end">30</div>
            <div className="p-[2px] flex flex-row gap-1 align-bottom">
              <div
                className={`w-[10px] h-[10px] bg-[#FFE0E0] cursor-pointer border ${selectedAgingBracket === AgingBracket.DAY_30 ? 'border-black border-2' : 'border-transparent'}`}
                onClick={() => handleAgingBracketClick(AgingBracket.DAY_30)}
                title="Filter by 30-59 days overdue"
              />
              <div
                className={`w-[10px] h-[10px] bg-[#FFAD0D] cursor-pointer border ${selectedAgingBracket === AgingBracket.DAY_60 ? 'border-black border-2' : 'border-transparent'}`}
                onClick={() => handleAgingBracketClick(AgingBracket.DAY_60)}
                title="Filter by 60-89 days overdue"
              />
              <div
                className={`w-[10px] h-[10px] bg-[#A41919] cursor-pointer border ${selectedAgingBracket === AgingBracket.DAY_90 ? 'border-black border-2' : 'border-transparent'}`}
                onClick={() => handleAgingBracketClick(AgingBracket.DAY_90)}
                title="Filter by 90+ days overdue"
              />
            </div>
            <div className="text-xs leading-none self-end">90 Days</div>
          </div>
        </div>
      </div>
    ),
    [
      summaryData,
      selectedAgingBracket,
      handleAgingBracketClick,
      selectedInvoices,
      selectedInvoicesData,
      handleBulkMerge,
      handleBulkExport,
      handleMarkAsFailed,
    ]
  );

  const getInvoiceDetailsColumns = (): ColumnDef<AccountsReceivableInvoice>[] => [
    {
      accessorKey: 'details',
      header: 'Invoice Details',
      size: 100,
      cell: (cell) => {
        const description = cell.row.original?.description;
        const adjustAmount = cell.row.original?.adjustAmount ?? 0;
        const balance = cell.row.original?.balance ?? 0.0;
        const memo = cell.row.original?.memo;

        return (
          <div className="flex gap-4 justify-between">
            <div className="flex flex-row w-[30%] gap-2 align-middle items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Description:</span>
              <span className="break-words flex-1">{description ?? '-'}</span>
            </div>
            <div className="flex flex-row w-[20%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Adjust Amount:</span>
              <span className="whitespace-nowrap">
                {adjustAmount && adjustAmount !== 0 ? formatCurrency(adjustAmount) : '-'}
              </span>
            </div>
            <div className="flex flex-row w-[15%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Balance:</span>
              <BillingAmount value={balance} className="whitespace-nowrap" />
            </div>
            <div className="flex flex-row w-[30%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Memo:</span>
              <span className="break-words flex-1">{memo ?? '-'}</span>
            </div>
          </div>
        );
      },
    },
  ];

  const columns: ColumnDef<AccountsReceivableInvoice>[] = useMemo(() => {
    return [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all"
            checked={selectAll}
            onCheckedChange={(checked) => handleSelectAllChange(checked as boolean)}
          />
        ),
        size: 136,

        cell: ({ row }) => {
          const isChecked = selectedInvoices.includes(row.original.invoiceNumber);
          return (
            <div className="flex w-full min-w-[40px] mr-5">
              <Checkbox
                id={`client-${row.original.clientId}`}
                checked={isChecked}
                onCheckedChange={(checked) => handleCheckboxChange(row.original.invoiceNumber, checked)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'code',
        header: 'Client #',
        enableSorting: true,
        cell: ({ cell }) => {
          const clientId = cell.row.original.clientId;
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{clientId}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'billingDate',
        header: 'Billing Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const billingdate = cell.getValue() as string;
          return (
            <div className="flex w-full min-w-[120px] mr-5">
              <span>{convertISODateToMMDDYYYY(billingdate)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice #',
        enableSorting: true,
        cell: ({ cell }) => {
          const invoiceNumber = cell.getValue() as string;
          return (
            <div
              className="flex items-center w-full min-w-[120px] cursor-pointer hover:text-focuscolor"
              tabIndex={0}
              onClick={() => {
                navigate(
                  resolveInvoiceRoute(cell.row.original.serviceType, invoiceNumber, cell.row.original.workOrderId)
                );
              }}
            >
              <span className="hover:underline">{invoiceNumber}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'fileNo',
        header: 'File #',
        enableSorting: true,
        cell: ({ cell }) => {
          const file = cell.getValue() as string | null;
          return <OneLineText text={file ?? '-'} className="w-full min-w-[100px] mr-5" />;
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ cell }) => {
          const amount = cell.row.original.invoiceAmount;
          return (
            <div className="flex w-full min-w-[120px] mr-5">
              <BillingAmount value={amount} />
            </div>
          );
        },
      },
      {
        accessorKey: 'invoiceStatus',
        header: 'Invoice Status',
        cell: ({ cell }) => {
          const status = cell.row.original.invoiceStatus;
          const agingBracket = cell.row.original.aging?.bracket;
          const isFailedTransaction = cell.row.original.isFailedTransaction;
          const badgeConfig = status === ARInvoiceStatus.PAID ? badges.PAID : badges[agingBracket ?? 'LESS_THAN_30'];

          return (
            <div className="flex w-full min-w-[120px] justify-start gap-2 items-center">
              <Badge
                className={`bg-[${badgeConfig.bgColor}] text-[${badgeConfig.fontColor}] hover:bg-[${badgeConfig.hoverColor}] mr-5 ${badgeClasses}`}
                style={{ backgroundColor: badgeConfig.bgColor, color: badgeConfig.fontColor }}
              >
                {badgeConfig.label}
              </Badge>
              {isFailedTransaction && <TriangleAlert className="text-[#D72222] h-4 w-4" />}
            </div>
          );
        },
      },

      ...(hasApplyPaymentFeature
        ? [
            {
              accessorKey: 'applyPayment',
              header: '',
              cell: ({ row }: { row: { original: AccountsReceivableInvoice } }) => {
                const invoice = row.original;
                const isUnpaid = invoice.invoiceStatus === ARInvoiceStatus.UNPAID;

                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`flex flex-row justify-between w-[115px] gap-2 min-w-[130px] ${isUnpaid ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                          onClick={() => isUnpaid && handleSingleInvoiceApplyPayment(invoice)}
                        >
                          <span className={`text-xs ${isUnpaid ? 'text-[#1551B2]' : 'text-gray-400'}`}>
                            Apply Payment
                          </span>
                          <SquareArrowUpRight className="h-4 w-4" color={isUnpaid ? '#0A2240' : '#9CA3AF'} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{isUnpaid ? 'Apply Payment to Invoice' : 'Invoice is already paid'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              },
            },
          ]
        : []),

      {
        accessorKey: 'invoiceDetails',
        header: '',
        cell: ({ cell }) => {
          const rowId = cell.row.original.invoiceNumber;
          const isExpanded = openSecondaryColumns.find((col) => col.id === rowId);
          return (
            <div
              className={`flex items-center flex-row min-w-[40px] justify-center cursor-pointer`}
              onClick={() => {
                if (isExpanded) {
                  const filtered = openSecondaryColumns.filter((col) => col.id !== rowId);
                  setOpenSecondaryColumns(filtered);
                } else {
                  setOpenSecondaryColumns((prev) => [...prev, { id: rowId, isExpanded: true }]);
                }
              }}
            >
              {isExpanded ? (
                <ChevronUp color="#676767" className="h-4 w-4" />
              ) : (
                <ChevronDown color="#676767" className="h-4 w-4" />
              )}
            </div>
          );
        },
        size: 75,
      },
    ];
  }, [
    selectedInvoices,
    selectAll,
    openSecondaryColumns,
    handleCheckboxChange,
    handleSelectAllChange,
    handleSingleInvoiceApplyPayment,
    navigate,
    tableData,
  ]);

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        toolbar={{
          toolBarTitle: 'Accounts Receivable',
          filters: viewFilters,
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => setTableData({ data: data as AccountsReceivableInvoice[], rest })}
        openSecondaryColumns={openSecondaryColumns}
        secondaryColumns={getInvoiceDetailsColumns}
        secondaryData={secondaryTableData}
        hideSecondaryHeaderRow
        search={searchConfig}
        summarySection={summarySection}
        getSecondaryTableRowId={(row) => row.invoiceNumber}
        filters={selectedAgingBracket ? { agingBracket: selectedAgingBracket } : undefined}
        key={refreshKey}
      />

      <ApplyPaymentModal
        open={isApplyPaymentModalOpen}
        onOpenChange={setIsApplyPaymentModalOpen}
        invoices={selectedInvoicesData}
        onSuccess={(data) => {
          handleApplyPaymentSuccess(data);
        }}
      />
    </div>
  );
};
export default AccountsReceivable;
